import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import type {
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
} from 'react';
import { desktopSize } from '@/lib/dom';
import { useFsVersion } from '@/fs/mutate';
import { fsApi } from '@/fs/useFs';
import { startItemDrag } from '@/dnd/drag';
import {
  confirmDelete,
  dropTargetProps,
  folderBehind,
  performDrop,
  registerSurface,
} from '@/dnd/drop';
import type { DropKind, DropPlacement, DropTarget } from '@/dnd/drop';
import {
  EMPTY_SELECTION,
  press,
  prune,
  release,
  selectAll,
  selectOnly,
  sweep,
} from '@/dnd/selection';
import type { SelectionState } from '@/dnd/selection';
import { bandHits, useMarquee } from '@/dnd/useMarquee';
import dnd from '@/dnd/dnd.module.css';
import { isRecycleBinNode, recycleBinIcon, useRecycleStore } from '@/recycle/store';
import { showContextMenu } from '@/shell/ContextMenu/ContextMenu';
import { compareNodes, desktopIconMenu, desktopMenu } from '@/shell/ContextMenu/menus';
import type { ArrangeKey } from '@/shell/ContextMenu/menus';
import { TASKBAR_H } from '@/wm/constants';
import { useWindowStore } from '@/wm/store';
import type { FsNode } from '@/fs/types';
import { DESKTOP_ICON_SIZE, DesktopIcon } from './DesktopIcon';
import {
  cellAt,
  cellKey,
  flowIndexAt,
  layoutIcons,
  placeDropped,
  reorder,
  snapToGrid,
} from './layout';
import type { Drop, Placements } from './layout';
import css from './Desktop.module.css';

/**
 * Identifies the desktop to a drag. A drop back onto the surface it started
 * from is a rearrangement; the same drop arriving from a folder window is a
 * move onto the desktop, and both come through `handleDrop` below.
 */
const OWNER = 'desktop';

/** Order icons by a remembered arrangement, leaving newcomers at the end. */
function inOrder(nodes: FsNode[], order: readonly string[]): FsNode[] {
  const rank = new Map(order.map((path, index) => [path, index]));
  const place = (node: FsNode) => rank.get(node.path) ?? Number.MAX_SAFE_INTEGER;
  return [...nodes].sort((a, b) => place(a) - place(b));
}

/** What dropping something on this icon would mean, if anything. */
function dropFor(node: FsNode): { kind: DropKind; path: string } | undefined {
  if (isRecycleBinNode(node)) return { kind: 'recycle', path: '' };
  const folder = folderBehind(node);
  return folder ? { kind: 'folder', path: folder } : undefined;
}

export function DesktopIcons() {
  const layerRef = useRef<HTMLDivElement>(null);
  /** Live icon elements, for hit-testing the rubber band against. */
  const elements = useRef(new Map<string, HTMLElement>());

  const [size, setSize] = useState(() => desktopSize(TASKBAR_H));
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  /** Where the user has dragged icons to, in the icon layer's own pixels. */
  const [placed, setPlaced] = useState<Placements>({});
  /** The sequence icons flow in once one has been dragged under Auto Arrange. */
  const [order, setOrder] = useState<readonly string[] | null>(null);

  /* ------------------------------------------------------- Arrange Icons By */

  const [arrange, setArrange] = useState<ArrangeKey | null>(null);
  const [autoArrange, setAutoArrange] = useState(false);
  // Now that icons can be dragged loose, this one does something: with it off
  // an icon stays exactly where it was dropped, and turning it back on pulls
  // everything that was dropped loose back onto the grid.
  const [alignToGrid, setAlignToGrid] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  // `fs.list()` already hides everything the Recycle Bin has swallowed, but it
  // is a plain function call — this subscription is what tells React that its
  // answer has changed. `version` is the same signal for a file dragged into
  // or out of the Desktop folder, and `refreshKey` for the Refresh command.
  const deleted = useRecycleStore((s) => s.deleted);
  const version = useFsVersion();

  // The desktop is a window too, and it is the active one exactly when nothing
  // else is — so opening something from an icon hands the focus away and the
  // selection left behind greys out, the way any unfocused list's does.
  const windowFocused = useWindowStore((s) => s.windows.some((w) => w.focused));

  // Separate from `deleted`, which Empty leaves alone: the bin can go from
  // full to empty without a single path changing state.
  const binFull = useRecycleStore((s) => s.items.length > 0);

  const nodes = useMemo(() => {
    const listed = fsApi.list(fsApi.roots.desktop);
    if (arrange) return [...listed].sort((a, b) => compareNodes(a, b, arrange));
    return order ? inOrder(listed, order) : listed;
  }, [arrange, order, deleted, version, refreshKey]);

  const slots = useMemo(
    () =>
      layoutIcons(nodes, {
        height: size.h,
        placed,
        honourPlaced: !autoArrange,
        honourFixed: !autoArrange && arrange === null,
      }),
    [nodes, size.h, placed, autoArrange, arrange],
  );

  /** Every icon on screen, in the order the eye reads them. */
  const paths = useMemo(() => slots.map((slot) => slot.node.path), [slots]);

  // Read by pointer handlers that outlive the render they were made in.
  const pathsRef = useRef(paths);
  const selectionRef = useRef(selection);
  const slotsRef = useRef(slots);
  useEffect(() => {
    pathsRef.current = paths;
    selectionRef.current = selection;
    slotsRef.current = slots;
  });

  // An icon dragged off the desktop, or into the bin, must not stay selected.
  useEffect(() => {
    setSelection((current) => prune(current, paths));
  }, [paths]);

  useEffect(() => {
    const el = layerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect) setSize({ w: rect.width, h: rect.height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const nodeAt = useCallback(
    (path: string): FsNode | undefined =>
      slotsRef.current.find((slot) => slot.node.path === path)?.node,
    [],
  );

  const selectedNodes = useCallback(
    (): FsNode[] =>
      [...selectionRef.current.paths]
        .map(nodeAt)
        .filter((node): node is FsNode => node !== undefined),
    [nodeAt],
  );

  const iconFor = useCallback(
    (node: FsNode): string =>
      isRecycleBinNode(node) ? recycleBinIcon(binFull) : fsApi.iconFor(node),
    [binFull],
  );

  /* ---------------------------------------------------------------- drag */

  /** Client coordinates into the icon layer's own, which is what a slot uses. */
  const toLayer = useCallback((places: readonly Drop[]) => {
    const layer = layerRef.current;
    if (!layer) return null;
    const box = layer.getBoundingClientRect();
    return {
      box,
      drops: places.map((place) => ({
        path: place.path,
        x: place.x - box.left,
        y: place.y - box.top,
      })),
    };
  }, []);

  /** Pin these icons where they were dropped, snapping if Align to Grid is on. */
  const placeAt = useCallback(
    (drops: readonly Drop[], box: DOMRect) => {
      const moving = new Set(drops.map((drop) => drop.path));
      const taken = new Set(
        slotsRef.current
          .filter((slot) => !moving.has(slot.node.path))
          .map((slot) => {
            const cell = cellAt(slot);
            return cellKey(cell.col, cell.row);
          }),
      );
      setPlaced((current) => ({
        ...current,
        ...placeDropped(drops, {
          taken,
          width: box.width,
          height: box.height,
          align: alignToGrid,
        }),
      }));
    },
    [alignToGrid],
  );

  // A file dragged out of a folder window and dropped on the desktop lands
  // where it was let go, rather than at the end of the grid.
  useEffect(
    () =>
      registerSurface(OWNER, (landings) => {
        const at = toLayer(landings.map((l) => ({ path: l.node.path, x: l.x, y: l.y })));
        if (at) placeAt(at.drops, at.box);
      }),
    [placeAt, toLayer],
  );

  /**
   * A drag that ended. Anywhere but this desktop is a move — into a folder, a
   * folder window, or the Recycle Bin — and `performDrop` owns every one of
   * those. What is left is the desktop rearranging its own icons.
   */
  const handleDrop = useCallback(
    (target: DropTarget | null, places: DropPlacement[]) => {
      if (!target || places.length === 0) return;

      if (target.kind !== 'surface' || target.owner !== OWNER) {
        const dragged = places
          .map((place) => nodeAt(place.path))
          .filter((node): node is FsNode => node !== undefined);
        performDrop(target, dragged, places);
        return;
      }

      const at = toLayer(places);
      if (!at) return;

      // Auto Arrange never lets an icon sit anywhere but in the grid, so a
      // drag under it can only be asking for a different place in the order.
      if (autoArrange) {
        setOrder(
          reorder(
            pathsRef.current,
            at.drops.map((drop) => drop.path),
            flowIndexAt(at.drops[0], at.box.height),
          ),
        );
        return;
      }

      placeAt(at.drops, at.box);
    },
    [autoArrange, nodeAt, placeAt, toLayer],
  );

  const registerIcon = (path: string) => (el: HTMLElement | null) => {
    if (el) elements.current.set(path, el);
    else elements.current.delete(path);
  };

  function handleIconPointerDown(node: FsNode, event: ReactPointerEvent<HTMLButtonElement>) {
    // The right button only ever selects; the menu comes on contextmenu.
    if (event.button !== 0) {
      setSelection((current) => (current.paths.has(node.path) ? current : selectOnly(node.path)));
      return;
    }

    const mods = { ctrl: event.ctrlKey || event.metaKey, shift: event.shiftKey };
    const next = press(selectionRef.current, node.path, pathsRef.current, mods);
    setSelection(next);
    selectionRef.current = next;
    // Ctrl-clicking an icon *off* is not the beginning of a drag.
    if (!next.paths.has(node.path)) return;

    // The pressed icon leads, so a drop can read its landing place as the one
    // the user was aiming with.
    const dragging = [node.path, ...[...next.paths].filter((path) => path !== node.path)];
    const items = dragging
      .map(nodeAt)
      .filter((found): found is FsNode => found !== undefined)
      .map((found) => ({
        node: found,
        icon: iconFor(found),
        iconSize: DESKTOP_ICON_SIZE,
        el: elements.current.get(found.path) ?? null,
      }));

    startItemDrag(event, {
      owner: OWNER,
      items,
      onDrop: handleDrop,
      onTap: () => setSelection((current) => release(current, node.path, mods)),
    });
  }

  /**
   * Focus, and the keyboard's way into a context menu. Both mean "this icon is
   * the one" — but neither should break up a selection the icon is already
   * part of, or right-clicking three chosen icons would act on one.
   */
  const handleSelect = useCallback((node: FsNode) => {
    setSelection((current) => (current.paths.has(node.path) ? current : selectOnly(node.path)));
  }, []);

  const handleOpen = useCallback((node: FsNode) => {
    fsApi.openFile(node);
  }, []);

  const handleIconContextMenu = useCallback(
    (node: FsNode, event: ReactMouseEvent<HTMLButtonElement>) => {
      // Right-clicking inside a multiple selection acts on all of it; anywhere
      // else the press above has already narrowed it to the one icon.
      const chosen = selectionRef.current.paths.has(node.path) ? selectedNodes() : [node];
      showContextMenu(event, desktopIconMenu(node, chosen));
    },
    [selectedNodes],
  );

  /* -------------------------------------------------------- the rubber band */

  const bandBase = useRef<SelectionState>(EMPTY_SELECTION);
  const marquee = useMarquee({
    boundsRef: layerRef,
    onStart: (additive) => {
      bandBase.current = additive ? selectionRef.current : EMPTY_SELECTION;
    },
    onSweep: (rect, additive) => {
      const hits = pathsRef.current.filter((path) => {
        const el = elements.current.get(path);
        return el ? bandHits(rect, el.getBoundingClientRect()) : false;
      });
      setSelection(sweep(bandBase.current, hits, additive));
    },
  });

  /* ------------------------------------------------------------- the layer */

  function handlePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    // Cells are `pointer-events: none`, so anything that isn't an icon lands
    // here directly — that's a press on bare desktop.
    if (event.target !== event.currentTarget) return;
    // Focus follows the press, so Delete has somewhere to arrive after a band
    // has swept up a selection without ever touching an icon.
    event.currentTarget.focus({ preventScroll: true });
    if (event.button === 0 && !(event.ctrlKey || event.metaKey || event.shiftKey)) {
      setSelection(EMPTY_SELECTION);
    }
    marquee.onPointerDown(event);
  }

  function handleKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Delete') {
      const chosen = selectedNodes();
      if (chosen.length === 0) return;
      event.preventDefault();
      confirmDelete(chosen, { permanent: event.shiftKey });
      return;
    }
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a') {
      event.preventDefault();
      setSelection(selectAll(pathsRef.current));
    }
  }

  function handleContextMenu(event: ReactMouseEvent<HTMLDivElement>) {
    // Same test: an icon raises its own menu and stops the event, so reaching
    // this handler at all means the click was on bare desktop.
    if (event.target !== event.currentTarget) return;
    setSelection(EMPTY_SELECTION);
    showContextMenu(
      event,
      desktopMenu({
        arrange,
        autoArrange,
        alignToGrid,
        onArrange: setArrange,
        onToggleAutoArrange: () => setAutoArrange((on) => !on),
        onToggleAlignToGrid: () => {
          const on = !alignToGrid;
          setAlignToGrid(on);
          // Turning it back on pulls everything that was dropped loose back
          // into the grid, which is what the setting means.
          if (on) setPlaced(snapToGrid);
        },
        // Nothing here is stale, but Refresh has always been the button people
        // press when a desktop looks wrong, so it really does rebuild the list.
        onRefresh: () => setRefreshKey((n) => n + 1),
      }),
    );
  }

  return (
    <div
      ref={layerRef}
      className={clsx(css.icons, windowFocused && css.iconsInactive)}
      data-desktop-surface=""
      // Focusable but not in the tab order: the icons themselves are the tab
      // stops, and this is only ever focused by pressing bare desktop.
      tabIndex={-1}
      {...dropTargetProps('surface', OWNER, fsApi.roots.desktop)}
      onPointerDown={handlePointerDown}
      onKeyDown={handleKeyDown}
      onContextMenu={handleContextMenu}
    >
      {slots.map(({ node, x, y }) => (
        <div key={node.path} className={css.slot} style={{ left: x, top: y }}>
          <DesktopIcon
            ref={registerIcon(node.path)}
            node={node}
            selected={selection.paths.has(node.path)}
            icon={isRecycleBinNode(node) ? recycleBinIcon(binFull) : undefined}
            drop={dropFor(node)}
            owner={OWNER}
            onPointerDown={handleIconPointerDown}
            onSelect={handleSelect}
            onOpen={handleOpen}
            onContextMenu={handleIconContextMenu}
          />
        </div>
      ))}

      {marquee.band ? <div className={dnd.band} style={marquee.band} aria-hidden="true" /> : null}
    </div>
  );
}
